import type { RequestContextSnapshot } from '../../requestContextSnapshot';

/**
 * Field rules copied from `@highereducation/request-context`.
 *
 * The published package does not import that module: its declaration
 * build follows the contract's const types and api-extractor stops.
 * `projectRequestContext.test.ts` fails when these rules, or the
 * unknown-value behavior, drift from `readRequestContextValue`.
 */
const REQUEST_FIELDS = [
  {
    key: 'partnerTripId',
    maxLength: 36,
    pattern:
      '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$',
  },
  {
    key: 'sessionState',
    maxLength: 10,
    pattern: '^(?:signed-in|signed-out)$',
  },
  { key: 'experiment', maxLength: 2, pattern: '^rv$' },
  { key: 'bot', maxLength: 2, pattern: '^(?:[1-9]|[1-9][0-9])$' },
  {
    key: 'consent',
    maxLength: 29,
    pattern: '^(?:C000[1-5])(?:,C000[1-5])*$',
  },
] as const satisfies readonly {
  key: keyof RequestContextSnapshot;
  maxLength: number | null;
  pattern: string | null;
}[];

const UNKNOWN = 'unknown';

function readValue(
  raw: unknown,
  rule: { maxLength: number | null; pattern: string | null },
): string {
  if (typeof raw !== 'string') {
    return UNKNOWN;
  }

  const value = raw.trim();
  if (value.length === 0) {
    return UNKNOWN;
  }

  if (rule.maxLength !== null && value.length > rule.maxLength) {
    return UNKNOWN;
  }

  if (rule.pattern !== null && !new RegExp(rule.pattern).test(value)) {
    return UNKNOWN;
  }

  return value;
}

/**
 * Allowlisted `context.request` snapshot.
 *
 * Always returns the five contract keys. A missing snapshot, a blank
 * string, a non-string, or a value that fails the field's length or
 * pattern becomes "unknown". Cookies, authorization, header names, and
 * any other property on the input are dropped.
 */
export function projectRequestContext(
  input?: object | null,
): RequestContextSnapshot {
  const snapshot: RequestContextSnapshot = {
    partnerTripId: UNKNOWN,
    sessionState: UNKNOWN,
    experiment: UNKNOWN,
    bot: UNKNOWN,
    consent: UNKNOWN,
  };
  if (input == null) {
    return snapshot;
  }

  const record = input as Record<string, unknown>;
  for (const field of REQUEST_FIELDS) {
    snapshot[field.key] = readValue(record[field.key], field);
  }
  return snapshot;
}
